import { format } from "date-fns";
import { EllipsisVerticalIcon, TrashIcon } from "lucide-react";
import React from "react";

import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PermissionGuard,
} from "@/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useI18n } from "@/context/i18n";
import { usePermissions } from "@/context/permissions";
import { formatCpfCnpj } from "@/core/utils";

import {
  getInsuranceCompanyTypeLabel,
  TInsuranceCompanies,
} from "../insurance-companies.type";

interface ITableInsuranceCompaniesProps {
  insuranceCompanies: TInsuranceCompanies[];
  deleteInsuranceCompany: (id: string) => void;
  editInsuranceCompany: (id: string) => void;
}

export const TableInsuranceCompanies: React.FC<
  ITableInsuranceCompaniesProps
> = ({ insuranceCompanies, deleteInsuranceCompany, editInsuranceCompany }) => {
  const { t } = useI18n();
  const { hasAnyPermission } = usePermissions();

  // Verificar se o usuário tem pelo menos uma permissão para ações
  const hasActionPermissions = hasAnyPermission(["insurance:edit", "insurance:delete"]);

  return (
    <div>
      <Card className="p-4">
        <CardContent>
          {insuranceCompanies.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-muted-foreground">
                {t("insuranceCompanies.no_insurance_companies")}
              </p>
            </div>
          )}
          {insuranceCompanies.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("clients.form.fields.name")}</TableHead>
                  <TableHead>{t("insuranceCompanies.cnpj")}</TableHead>
                  <TableHead>
                    {t("insuranceCompanies.corporate_name")}
                  </TableHead>
                  <TableHead>{t("insuranceCompanies.type")}</TableHead>
                  <TableHead>{t("generic.created_at")}</TableHead>
                  {hasActionPermissions && (
                    <TableHead>{t("generic.actions")}</TableHead>
                  )}
                </TableRow>
              </TableHeader>

              <TableBody>
                {insuranceCompanies.map((insuranceCompany, i) => (
                  <TableRow key={i}>
                    <TableCell>{insuranceCompany.name}</TableCell>
                    <TableCell>
                      {formatCpfCnpj(insuranceCompany.cnpj)}
                    </TableCell>
                    <TableCell>{insuranceCompany.corporate_name}</TableCell>
                    <TableCell>
                      {getInsuranceCompanyTypeLabel(insuranceCompany.type, t)}
                    </TableCell>
                    <TableCell>
                      {format(
                        new Date(insuranceCompany.created_at || ""),
                        "dd/MM/yyyy"
                      )}
                    </TableCell>
                    {hasActionPermissions && (
                      <TableCell>
                        <PermissionGuard
                          permissions={["insurance:edit", "insurance:delete"]}
                          requireAll={false}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger>
                              <Button size={"sm"} variant={"ghost"}>
                                <EllipsisVerticalIcon className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <PermissionGuard permission="insurance:edit">
                                <DropdownMenuItem
                                  onSelect={() =>
                                    editInsuranceCompany(insuranceCompany.id || "")
                                  }
                                >
                                  {t("generic.edit")}
                                </DropdownMenuItem>
                              </PermissionGuard>
                              <PermissionGuard permission="insurance:delete">
                                <DropdownMenuItem>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() =>
                                      deleteInsuranceCompany(
                                        insuranceCompany.id || ""
                                      )
                                    }
                                  >
                                    <TrashIcon className="h-4 w-4 mr-2" />
                                    {t("generic.delete")}
                                  </Button>
                                </DropdownMenuItem>
                              </PermissionGuard>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </PermissionGuard>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
